import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { TimeUtils } from '@freelancer/time-utils';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { CalloutPlacement, CalloutSize } from '@freelancer/ui/callout';
import { CameraComponent } from '@freelancer/ui/camera';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { IconSize } from '@freelancer/ui/icon';
import { Margin } from '@freelancer/ui/margin';
import { PictureDisplay } from '@freelancer/ui/picture';
import {
  FontColor,
  FontSize,
  FontType,
  FontWeight,
  TextTransform,
} from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import { last, map, take } from 'rxjs/operators';

@Component({
  template: `
    <fl-bit [flHide]="!showContent">
      <fl-heading
        i18n="Use your camera modal title"
        [flMarginBottom]="Margin.SMALL"
        [headingType]="HeadingType.H4"
        [size]="HeadingSize.XSMALL"
      >
        Use your camera to take photo
      </fl-heading>
      <fl-bit [flMarginBottom]="Margin.SMALL">
        <fl-text
          i18n="An example for taking pictures using camera"
          [color]="FontColor.MID"
          [fontType]="FontType.SPAN"
        >
          See example
        </fl-text>
        <fl-callout
          [placement]="CalloutPlacement.RIGHT"
          [size]="CalloutSize.MEDIUM"
        >
          <fl-callout-trigger>
            <fl-icon [name]="'ui-help'" [size]="IconSize.SMALL"></fl-icon>
          </fl-callout-trigger>
          <fl-callout-content>
            <fl-heading
              i18n="Instructions for taking photo"
              [flMarginBottom]="Margin.SMALL"
              [headingType]="HeadingType.H3"
              [size]="HeadingSize.XSMALL"
            >
              Instructions
            </fl-heading>
            <fl-text
              i18n="Instructions for taking keycode verification photo"
              [flMarginBottom]="Margin.MID"
            >
              Take a photo of yourself holding the
              <fl-text [fontType]="FontType.STRONG">valid ID</fl-text>
              and
              <fl-text [fontType]="FontType.STRONG">
                your keycode side-by-side
              </fl-text>
            </fl-text>
            <fl-text
              i18n="Image reference when taking photo for keycode verification"
              [color]="FontColor.MID"
              [flMarginBottom]="Margin.SMALL"
              [size]="FontSize.SMALL"
              [textTransform]="TextTransform.UPPERCASE"
            >
              Refer to this image when taking your keycode verification photo
            </fl-text>
            <fl-picture
              alt="Keycode instructions"
              i18n-alt="Image for taking keycode instructions photo"
              [src]="'kyc/keycode-instructions.png'"
              [boundedWidth]="true"
            ></fl-picture>
          </fl-callout-content>
        </fl-callout>
      </fl-bit>
      <fl-bit
        class="CameraView"
        [flHide]="!showCamera"
        [flMarginBottom]="Margin.SMALL"
      >
        <fl-camera
          [videoDevices]="videoDevices"
          [videoConstraints]="videoConstraints"
        ></fl-camera>
        <fl-text
          class="CameraView-timer"
          *ngIf="showCameraTimer"
          i18n="Timer countdown when taking photo"
          [color]="FontColor.INHERIT"
          [size]="FontSize.INHERIT"
          [weight]="FontWeight.BOLD"
        >
          {{ cameraTimerValue$ | async }}
        </fl-text>
        <fl-button
          class="CameraView-switcher"
          *ngIf="showCameraSwitcher"
          [color]="ButtonColor.DEFAULT"
          [disabled]="showCameraTimer"
          [size]="ButtonSize.SMALL"
          (click)="cycleDevice()"
        >
          <fl-icon [name]="'ui-camera-switch'"></fl-icon>
        </fl-button>
      </fl-bit>
      <fl-picture
        *ngIf="!showCamera"
        [alignCenter]="true"
        [boundedWidth]="true"
        [display]="PictureDisplay.BLOCK"
        [externalSrc]="true"
        [flMarginBottom]="Margin.SMALL"
        [src]="imagePreviewSrc"
      ></fl-picture>
      <fl-bit class="CameraView-footer">
        <fl-button
          class="CameraView-action"
          *ngIf="showCamera"
          i18n="Take photo button"
          [color]="ButtonColor.SECONDARY"
          [disabled]="showCameraTimer || isSwitchingCamera"
          [size]="ButtonSize.LARGE"
          [sizeTablet]="ButtonSize.XLARGE"
          (click)="takePhoto()"
        >
          Take Photo
        </fl-button>
        <fl-button
          class="CameraView-action"
          *ngIf="!showCamera"
          i18n="Retake button"
          [color]="ButtonColor.DEFAULT"
          [flMarginRight]="Margin.SMALL"
          [size]="ButtonSize.LARGE"
          [sizeTablet]="ButtonSize.XLARGE"
          (click)="retakePhoto()"
        >
          Retake
        </fl-button>
        <fl-button
          class="CameraView-action"
          *ngIf="!showCamera"
          i18n="Upload button"
          [color]="ButtonColor.SECONDARY"
          [flMarginBottom]="Margin.SMALL"
          [flMarginBottomTablet]="Margin.NONE"
          [size]="ButtonSize.LARGE"
          [sizeTablet]="ButtonSize.XLARGE"
          (click)="confirm()"
        >
          Upload
        </fl-button>
      </fl-bit>
    </fl-bit>
  `,
  styleUrls: ['./camera-photo-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CameraPhotoModalComponent implements AfterViewInit, OnDestroy {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CalloutPlacement = CalloutPlacement;
  CalloutSize = CalloutSize;
  FontColor = FontColor;
  FontSize = FontSize;
  FontType = FontType;
  FontWeight = FontWeight;
  HeadingSize = HeadingSize;
  HeadingType = HeadingType;
  IconSize = IconSize;
  Margin = Margin;
  PictureDisplay = PictureDisplay;
  TextTransform = TextTransform;

  cameraTimerValue$: Rx.Observable<number>;
  imagePreviewSrc: string | undefined = undefined;
  remainingCaptureTime = 0;
  showCamera = false;
  showCameraSwitcher = false;
  showCameraTimer = false;
  showContent = false;
  isSwitchingCamera = false;

  private cameraTimerSubscription: Rx.Subscription;

  @Input() captureTimeout = 5; // in seconds
  @Input() videoDevices: ReadonlyArray<MediaDeviceInfo>;
  @Input() videoConstraints: ReadonlyArray<MediaTrackConstraints>;

  @ViewChild(CameraComponent) camera: CameraComponent;

  constructor(
    private modalRef: ModalRef<CameraPhotoModalComponent>,
    private timeUtils: TimeUtils,
  ) {}

  ngAfterViewInit() {
    this.cycleDevice();
    this.setShowCamera(true);
    this.showContent = true;
  }

  ngOnDestroy() {
    if (this.cameraTimerSubscription) {
      this.cameraTimerSubscription.unsubscribe();
    }
  }

  cancel() {
    this.modalRef.close();
  }

  confirm() {
    this.modalRef.close(this.imagePreviewSrc);
  }

  cycleDevice() {
    this.setShowCameraSwitcher(false);
    this.isSwitchingCamera = true;
    this.camera.cycleDevice().then(() => {
      this.setShowCameraSwitcher(true);
      this.isSwitchingCamera = false;
    });
  }

  retakePhoto() {
    this.setShowCameraSwitcher(true);
    this.setShowCamera(true);
  }

  setShowCamera(value: boolean) {
    this.showCamera = value;
  }

  setShowCameraSwitcher(value: boolean) {
    this.showCameraSwitcher = value && this.videoDevices.length > 1;
  }

  setShowCameraTimer(value: boolean) {
    this.showCameraTimer = value;
  }

  takePhoto() {
    if (!this.showCameraTimer) {
      this.setShowCameraTimer(true);
      this.setShowCameraSwitcher(false);
      this.cameraTimerValue$ = this.timeUtils.rxTimer(0, 1000).pipe(
        take(this.captureTimeout + 1),
        map(val => this.captureTimeout - val),
      );

      this.cameraTimerSubscription = this.cameraTimerValue$
        .pipe(last(val => val >= this.captureTimeout))
        .subscribe(val => {
          this.imagePreviewSrc = this.camera.getSnapshot();
          this.setShowCameraTimer(false);
          this.setShowCamera(false);
        });
    }
  }
}
